import { getKitLabelWithoutCount } from "@src/helpers/functions";
import { Modal, Button } from "antd";
import {
  AiFillMinusCircle,
  AiFillPlusCircle,
  AiFillWarning,
} from "react-icons/ai";
import { MdAddCircleOutline } from "react-icons/md";
import ClientDeviceKitForm from "./client-kit-form/ClientDeviceKitForm";
import { useEffect, useState } from "react";
import {
  AppDispatch,
  deleteKitDevice,
  deleteKitDeviceClear,
  deleteKitDeviceRequest,
  getKitListRequest } from
"@src/redux";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDeviceTypes } from "@src/helpers/constants";
import { useCheckPageComponentAccess } from "@src/hooks/index";

type Props = {
  kit: any;
};

const ClientKitCardContent = (props: Props) => {
  const { kit } = props;
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isDeviceKitModalVisible, setIsDeviceKitModalVisible] =
  useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
  useState(false);
  const [selectedDeviceKit, setSelectedDeviceKit] = useState<string>();
  const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState("");

  const { result, error, loading } = useSelector(deleteKitDevice);

  const deviceLabels: any = {
    maximumVincenseDevices: "VinCense",
    maximumEcgDevices: "ECG",
    maximumGlucometerDevices: "Glucometer",
    maximumWeighingScaleDevices: "Weighing Scale",
    maximumBpMonitorDevices: "BP Monitor"
  };

  useEffect(() => {
    if (result?.status === "success") {
      getKitLists();
      handleCancelDelete();
      dispatch(deleteKitDeviceClear());
    }
  }, [result, error]);

  const handleDeviceModalCancel = () => {
    setIsDeviceKitModalVisible(false);
  };

  const handleDeviceModalOpen = (item: string, label: any) => {
    setIsDeviceKitModalVisible(true);
    setSelectedDeviceKit(item);
    setModalTitle(label);
  };

  const getKitLists = () => {
    dispatch(
      getKitListRequest({
        filter: {
          searchString: "",
          organizationId: id
        }
      })
    );
  };

  const deleteDevice = () => {
    dispatch(
      deleteKitDeviceRequest({
        id: deviceToDelete.id
      })
    );
  };

  const filterDevicesByType = (type: string) => {
    return (
      kit.kitDeviceList?.filter(
        (device: any) => device.device.type === type
      ) || []);
  };

  const handleRemoveDevice = (device: any) => {
    setDeviceToDelete(device);
    setIsDeleteModalVisible(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
  };

  return (
    <div className="client-kit-card-content">
      {Object.keys(kit).
      filter((key: any) => key.startsWith("maximum") && kit[key] > 0)?.
      map((item: any, index: any) => {
        const deviceType = getDeviceTypes[item];
        const devicesByType = filterDevicesByType(deviceType);
        const maxCount = kit[item];
        const currentCount = devicesByType.length;
        const isFull = currentCount >= maxCount;
        const canAdd =
          !isFull &&
          useCheckPageComponentAccess("add_kit_device_action");

        return (
          <div key={index} className="kit-device-group">
            <div className="kit-device-group-header">
              <div className="kit-device-group-title">
                <span className="kit-device-type-name">
                  {getKitLabelWithoutCount(item, kit)}
                </span>
                <span
                  className={`kit-device-count-badge${
                    isFull ? " is-full" : ""
                  }`}
                >
                  {currentCount} / {maxCount}
                </span>
              </div>

              {canAdd && (
                <button
                  type="button"
                  className="kit-device-add-btn"
                  aria-label="Add device"
                  onClick={() => handleDeviceModalOpen(kit.id, item)}
                >
                  <AiFillPlusCircle />
                </button>
              )}
            </div>

            {currentCount > 0 ? (
              <div className="kit-device-list">
                {devicesByType.map((device: any, deviceIndex: any) => (
                  <div key={deviceIndex} className="kit-device-row">
                    {useCheckPageComponentAccess(
                      "remove_kit_device_action"
                    ) && (
                      <button
                        type="button"
                        className="kit-device-remove-btn"
                        aria-label="Remove device"
                        onClick={() => handleRemoveDevice(device)}
                      >
                        <AiFillMinusCircle />
                      </button>
                    )}
                    <span className="kit-device-ref-id">
                      {device.device.referenceId}
                    </span>
                    <span className="kit-device-separator">•</span>
                    <span className="kit-device-mac">
                      {device.device.macAddress}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="kit-device-empty">No devices added</div>
            )}
          </div>
        );
      })}

      <Modal
        className="kit-edit-modal kit-edit-modal--danger"
        title={
          <span className="kit-edit-modal-title">
            <AiFillWarning style={{ marginRight: 8, verticalAlign: "-3px" }} />
            Remove Kit Device
          </span>
        }
        visible={isDeleteModalVisible}
        onCancel={handleCancelDelete}
        footer={null}
        width={460}
        centered
      >
        <div className="kit-confirm-body">
          <div className="kit-confirm-icon">
            <AiFillWarning />
          </div>
          <div className="kit-confirm-text">
            <div className="kit-confirm-heading">Are you sure?</div>
            <div className="kit-confirm-description">
              You are about to remove the device{" "}
              <strong>{deviceToDelete?.device?.referenceId}</strong>. This
              action cannot be undone.
            </div>
          </div>
        </div>
        <div className="kit-edit-modal-footer">
          <Button onClick={handleCancelDelete} danger>
            {t("close")}
          </Button>
          <Button onClick={deleteDevice} type="primary" loading={loading} danger>
            {t("remove")}
          </Button>
        </div>
      </Modal>

      <Modal
        className="kit-edit-modal"
        title={
          <span className="kit-edit-modal-title">
            <MdAddCircleOutline
              style={{ marginRight: 8, verticalAlign: "-3px" }}
            />
            {`Add ${deviceLabels[modalTitle] ?? ""} Device`}
          </span>
        }
        visible={isDeviceKitModalVisible}
        onCancel={handleDeviceModalCancel}
        destroyOnClose
        footer={null}
        width={520}
        centered
      >
        {selectedDeviceKit && (
          <ClientDeviceKitForm
            handleCancel={handleDeviceModalCancel}
            modalType="add"
            mutationType="edit"
            deviceKit={selectedDeviceKit}
            getKitLists={getKitLists}
            deviceType={getDeviceTypes?.[modalTitle]}
            kit={kit}
          />
        )}
      </Modal>
    </div>);

};

export default ClientKitCardContent;
